import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorCommand } from '@atlaskit/editor-common/types';
import {
  ListBulletedIcon,
  ListNumberedIcon,
  ToolbarButton,
  ToolbarButtonGroup,
  ToolbarKeyboardShortcutHint,
  ToolbarSection,
  ToolbarTooltip,
} from '@atlaskit/editor-toolbar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { ButtonItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { type DatabasesEditorAPI } from '../databases-preset';

import { POPUP_MENU_GROUP_WIDTH } from './toolbar-constants';
import { useToolbarShortcutKeys } from './use-toolbar-shortcut-keys';

interface ListSectionProps {
  editorApi: DatabasesEditorAPI;
  listState:
    | {
        bulletListActive?: boolean;
        bulletListDisabled?: boolean;
        orderedListActive?: boolean;
      }
    | undefined;
  execute: ((command: EditorCommand | undefined) => boolean) | undefined;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  preventBlur: React.MouseEventHandler;
}

export const ListSection: React.FC<ListSectionProps> = ({
  editorApi,
  listState,
  execute,
  isOpen,
  onToggle,
  onClose,
  preventBlur,
}) => {
  const { formatMessage } = useIntl();
  const { modHint, shiftHint } = useToolbarShortcutKeys();

  const toggleBulletList = useCallback(() => {
    execute?.(editorApi?.list?.commands.toggleBulletList(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleOrderedList = useCallback(() => {
    execute?.(editorApi?.list?.commands.toggleOrderedList(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  return (
    <ToolbarSection>
      <ToolbarButtonGroup>
        <ToolbarTooltip
          content={`${formatMessage(
            messages['rich-text-formatting-toolbar.bulleted-list'],
          )} ${modHint}${shiftHint}8`}
          position="top"
        >
          <ToolbarButton
            iconBefore={
              <ListBulletedIcon
                size="small"
                label={formatMessage(
                  messages['rich-text-formatting-toolbar.bullet-list'],
                )}
              />
            }
            isSelected={listState?.bulletListActive}
            isDisabled={listState?.bulletListDisabled}
            onClick={toggleBulletList}
            testId="bullet-list-button"
          />
        </ToolbarTooltip>

        <Popup
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom-start"
          autoFocus={false}
          shouldDisableFocusLock
          trigger={(triggerProps) => (
            <ToolbarTooltip
              content={formatMessage(messages['rich-text-formatting-toolbar.lists'])}
              position="top"
            >
              <ToolbarButton
                {...triggerProps}
                iconBefore={<ChevronDownIcon label="" size="small" />}
                label={formatMessage(
                  messages['rich-text-formatting-toolbar.more-list-options'],
                )}
                isSelected={isOpen}
                onClick={onToggle}
                testId="list-dropdown-trigger"
              />
            </ToolbarTooltip>
          )}
          content={() => (
            <Box as="div" onMouseDown={preventBlur}>
              <PopupMenuGroup minWidth={POPUP_MENU_GROUP_WIDTH}>
                <Section>
                  <ButtonItem
                    iconBefore={
                      <ListBulletedIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.bulleted-list'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint}8`} />
                    }
                    isSelected={listState?.bulletListActive}
                    onClick={toggleBulletList}
                  >
                    {formatMessage(
                      messages['rich-text-formatting-toolbar.bulleted-list'],
                    )}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <ListNumberedIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.numbered-list'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint}7`} />
                    }
                    isSelected={listState?.orderedListActive}
                    onClick={toggleOrderedList}
                  >
                    {formatMessage(
                      messages['rich-text-formatting-toolbar.numbered-list'],
                    )}
                  </ButtonItem>
                </Section>
              </PopupMenuGroup>
            </Box>
          )}
        />
      </ToolbarButtonGroup>
    </ToolbarSection>
  );
};
